<!--
  One icon system, drawn here rather than borrowed: a 24-unit grid, 1.75 stroke,
  round caps and joins. Two glyphs are solid (play, spark) because they read as
  buttons; everything else is a line.
-->
<script lang="ts" module>
  export type IconName =
    | 'search'
    | 'play'
    | 'pause'
    | 'back'
    | 'forward'
    | 'down'
    | 'up'
    | 'close'
    | 'check'
    | 'alert'
    | 'people'
    | 'link'
    | 'settings'
    | 'refresh'
    | 'sliders'
    | 'trash'
    | 'plus'
    | 'film'
    | 'tv'
    | 'clock'
    | 'star'
    | 'subtitles'
    | 'speaker'
    | 'mute'
    | 'external'
    /* Added by the SHELL agent for the two navigation bars. */
    | 'home'
    | 'user'
    | 'grid'
    | 'chat'
    | 'bookmark'
    | 'saved'
    | 'dots'
    | 'info'
    /* Added for the profile header's handle editor. */
    | 'pencil'
    /* Added for the player's Share chip: the room is always open, the button
       only hands out its address. */
    | 'share'
    /* Added for the phone title page's icon row and the Downloads screen. */
    | 'thumb'
    | 'thumbed'
    | 'download'
    | 'downloaded';

  /** Stroked paths, drawn on the same 24-unit grid. */
  const STROKE: Record<string, string> = {
    search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14M16.2 16.2 21 21',
    pause: 'M9 5v14M15 5v14',
    back: 'M15 4 7 12l8 8',
    forward: 'M9 4l8 8-8 8',
    down: 'M5 9l7 7 7-7',
    up: 'M5 15l7-7 7 7',
    close: 'M6 6l12 12M18 6L6 18',
    check: 'M4.5 12.5 9.5 17.5 19.5 6.5',
    alert: 'M12 8v5M12 16.6v.4M12 3.4 2.6 20h18.8z',
    people:
      'M9.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M2.5 20c0-3.3 3-5.5 7-5.5s7 2.2 7 5.5M16.2 4.6a3.5 3.5 0 0 1 0 6.8M18.4 14.8c1.9.7 3.1 2.2 3.1 4.2',
    link: 'M10.5 13.5a4 4 0 0 0 5.7 0l2.8-2.8a4 4 0 0 0-5.7-5.7l-1.4 1.4M13.5 10.5a4 4 0 0 0-5.7 0l-2.8 2.8a4 4 0 1 0 5.7 5.7l1.4-1.4',
    settings:
      'M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4M19.6 14.4a1.6 1.6 0 0 0 .3 1.8l.1.1a1.9 1.9 0 1 1-2.7 2.7l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.2a1.9 1.9 0 1 1-3.8 0v-.1a1.6 1.6 0 0 0-2.8-1.1l-.1.1a1.9 1.9 0 1 1-2.7-2.7l.1-.1a1.6 1.6 0 0 0-1.1-2.7h-.2a1.9 1.9 0 1 1 0-3.8h.1a1.6 1.6 0 0 0 1.1-2.8l-.1-.1a1.9 1.9 0 1 1 2.7-2.7l.1.1a1.6 1.6 0 0 0 1.8.3h.1a1.6 1.6 0 0 0 1-1.5v-.2a1.9 1.9 0 1 1 3.8 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a1.9 1.9 0 1 1 2.7 2.7l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.2a1.9 1.9 0 1 1 0 3.8h-.1a1.6 1.6 0 0 0-1.5 1z',
    refresh: 'M20 11a8 8 0 1 0-.7 4.6M20 4.5V11h-6.5',
    sliders: 'M4 7h10M18 7h2M4 17h4M12 17h8M16 4.5v5M8 14.5v5',
    trash: 'M4.5 6.5h15M9.5 6.5V4.2h5v2.3M6.6 6.5 7.5 20h9l.9-13.5M10.5 10v6M13.5 10v6',
    plus: 'M12 5v14M5 12h14',
    film: 'M3.5 5.5h17v13h-17zM8 5.5v13M16 5.5v13M3.5 12h17',
    tv: 'M3.5 7.5h17v11h-17zM8.5 3.5 12 7.5l3.5-4',
    clock: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16M12 7.5V12l3 2',
    star: 'M12 3.8l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.6 9.9l5.8-.8z',
    subtitles: 'M3.5 5.5h17v13h-17zM7 11h4M14 11h3M7 15h3M13 15h4',
    speaker: 'M4.5 9.5h3l4.5-4v13l-4.5-4h-3zM16 9.2a4 4 0 0 1 0 5.6M18.6 6.9a7.5 7.5 0 0 1 0 10.2',
    /* The silent twin of `speaker`: same cone, so a toggle does not jump. */
    mute: 'M4.5 9.5h3l4.5-4v13l-4.5-4h-3zM16.2 10.2l4.6 4.6M20.8 10.2l-4.6 4.6',
    external: 'M14 4.5h5.5V10M19 5l-8 8M18 14v5.5H4.5V6H10',
    home: 'M3.6 10.4 12 3.8l8.4 6.6V20.2H3.6zM9.4 20.2v-6.1h5.2v6.1',
    user: 'M12 11.6a3.9 3.9 0 1 0 0-7.8 3.9 3.9 0 0 0 0 7.8M4.4 20.4c0-3.6 3.4-6.1 7.6-6.1s7.6 2.5 7.6 6.1',
    grid: 'M4 4.4h6.4v6.4H4zM13.6 4.4H20v6.4h-6.4zM4 13.2h6.4v6.4H4zM13.6 13.2H20v6.4h-6.4z',
    chat: 'M4.2 5.4h15.6v10.4h-9.4L6 19.9v-4.1H4.2z',
    bookmark: 'M6.6 4.6h10.8v15l-5.4-3.9-5.4 3.9z',
    dots: 'M6 12h.01M12 12h.01M18 12h.01',
    info: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16M12 11.2v5.4M12 7.7v.3',
    /* A pencil at 45°: shaft, sharpened wedge at the point, one line across the
       ferrule so it reads as a pencil and not a wand at 14px. */
    pencil: 'M16.6 4.4a2 2 0 0 1 2.8 2.8L8.7 17.9l-4.2 1.4 1.4-4.2zM14.7 6.3l2.8 2.8',
    /* An arrow leaving a tray — the shape both platforms mean by "share". The
       arrow clears the tray's rim so the two read apart at 15px. */
    share: 'M12 14.6V3.6M8.2 7.2 12 3.4l3.8 3.8M8 10.8H5.2v9.6h13.6v-9.6H16',
    /* A thumb: the cuff at the base, the palm, and the thumb over the top. Drawn
       on the same 24-unit grid as everything else rather than borrowed, and the
       pressed twin below is the identical silhouette filled, so the toggle does
       not jump — the same discipline as bookmark/saved. */
    thumb: 'M8.4 20.2H5.4a1 1 0 0 1-1-1v-7.4a1 1 0 0 1 1-1h3zM8.4 10.8l3.4-6.9a2.1 2.1 0 0 1 3 2.6l-1 3.2h4.4a1.8 1.8 0 0 1 1.8 2.2l-1.3 6a1.8 1.8 0 0 1-1.8 1.4H8.4z',
    /* An arrow into a tray — the download shape both platforms use, and the
       deliberate inverse of `share`, whose arrow leaves the same tray. */
    download: 'M12 3.6v11M8.2 11l3.8 3.8L15.8 11M5.2 20.4h13.6'
  };

  /** Solid paths. */
  const FILL: Record<string, string> = {
    play: 'M7.5 4.8a1 1 0 0 1 1.5-.86l10.2 6.2a1 1 0 0 1 0 1.72L9 18.06a1 1 0 0 1-1.5-.86z',
    /* The pressed twin of `bookmark`: same silhouette, so a toggle does not jump. */
    saved:
      'M6.6 3.9a.75.75 0 0 0-.75.75v14.95a.75.75 0 0 0 1.19.61L12 16.53l4.96 3.68a.75.75 0 0 0 1.19-.61V4.65a.75.75 0 0 0-.75-.75z',
    /* The pressed twin of `thumb`, same silhouette filled. */
    thumbed:
      'M8.4 20.2H5.4a1 1 0 0 1-1-1v-7.4a1 1 0 0 1 1-1h3zM9.6 10.9l3.3-6.7a2.1 2.1 0 0 1 3 2.6l-1 3.2h4.3a1.8 1.8 0 0 1 1.8 2.2l-1.3 6a1.8 1.8 0 0 1-1.8 1.4H9.6z',
    /* The finished twin of `download`: the same tray, a tick where the arrow was. */
    downloaded:
      'M12 2.9a9.1 9.1 0 1 0 0 18.2 9.1 9.1 0 0 0 0-18.2m4.7 6.4-5.9 6.5a1 1 0 0 1-1.5 0L6.8 13a1 1 0 0 1 1.5-1.3l1.8 2 5.1-5.7a1 1 0 0 1 1.5 1.3'
  };
</script>

<script lang="ts">
  interface Props {
    name: IconName;
    /** Pixels. The stroke scales with it, so 16 and 28 look like one family. */
    size?: number;
    /** Extra classes for colour. Stroke and fill both use currentColor. */
    class?: string;
  }

  let { name, size = 20, class: klass = '' }: Props = $props();

  const solid = $derived(FILL[name]);
  const stroked = $derived(STROKE[name]);
</script>

<svg
  class={klass}
  width={size}
  height={size}
  viewBox="0 0 24 24"
  fill="none"
  stroke="none"
  aria-hidden="true"
  focusable="false"
>
  {#if solid}
    <path d={solid} fill="currentColor" />
  {:else if stroked}
    <path
      d={stroked}
      stroke="currentColor"
      stroke-width="1.75"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  {/if}
</svg>

<style>
  svg {
    display: block;
    flex: none;
  }
</style>
